// GradientWaves adapted from React Bits by David Haz; see THIRD_PARTY_NOTICES.md.
// The wave lighting is preserved; integer phase harmonics make the motion loop.
// This ambient renderer omits
// pointer effects and shares the site's motion preference instead of overriding it.
import { useEffect, useRef } from 'react';
import './GradientWaves.css';

const MAX_SWELLS = 4;
const CAMERA_HEIGHT = 30;
const MAX_PIXELS = 1_200_000;
const FRAME_INTERVAL = 1000 / 30;
const LOOP_SECONDS = 240;
const REFERENCE_SPEED = 0.07;
const QUALITY = { low: 56, medium: 84, high: 120 };

const VERTEX = `#version 300 es
in vec2 aPosition;
void main() {
  gl_Position = vec4(aPosition, 0.0, 1.0);
}`;

const FRAGMENT = `#version 300 es
precision highp float;
uniform vec2 uResolution;
uniform float uPhase;
uniform float uAmplitude;
uniform float uWaveScale;
uniform float uWaveRatio;
uniform float uSwell;
uniform float uTurbulence;
uniform float uHeight;
uniform float uTilt;
uniform float uZoom;
uniform vec2 uLook;
uniform float uFogDepth;
uniform float uBrightness;
uniform float uOpacity;
uniform float uGrain;
uniform vec3 uHorizon;
uniform vec3 uWave;
uniform vec3 uCrest;
uniform vec3 uBackground;
uniform float uOpaque;
uniform float uLight;
uniform vec3 uSun;
uniform float uRelief;
uniform float uSheen;
uniform float uSteps;
uniform float uStride;
uniform vec3 uIntro;
uniform vec3 uSwells[${MAX_SWELLS}];
uniform float uSwellWidth;
out vec4 outColor;

const float FAR = 4000.0;

float hash(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}

vec3 surface(vec2 xy) {
  vec2 freq = vec2(uWaveScale / 7.0, (uWaveScale * uWaveRatio) / 3.0);
  // Every time-dependent angle completes an integer number of turns per loop.
  // The heights, normals, and velocity therefore all join smoothly at the seam.
  // These harmonics retain the previous slow drift to within about 8 percent.
  vec4 phase = uPhase * vec4(2.0, 4.0, 3.0, 4.0);
  float swellPhase = (xy.y + xy.x) / 20.0 + phase.y;
  float bend = uSwell * cos(swellPhase) / 20.0;
  float mx = xy.x * freq.x + phase.x + uSwell * freq.x * sin(swellPhase);
  float crossPhase = xy.x / 23.0 + phase.w;
  float my = xy.y * freq.y - phase.z + uTurbulence * freq.y * cos(crossPhase);
  float twist = -uTurbulence * sin(crossPhase) / 23.0;
  float cx = cos(mx);
  float cy = cos(my);
  float rise = 1.0;
  if (uIntro.z > 0.0) rise = mix(1.0, smoothstep(uIntro.y * 0.45, uIntro.y * 1.6, xy.x), uIntro.z);
  float amp = uAmplitude * rise;
  float h = (sin(mx) + sin(my)) * amp + uHeight;
  float dx = amp * (freq.x * cx * (1.0 + bend) + freq.y * cy * twist);
  float dy = amp * (freq.x * cx * bend + freq.y * cy);
  for (int k = 0; k < ${MAX_SWELLS}; k++) {
    vec3 sw = uSwells[k];
    if (sw.z <= 0.0) continue;
    float u = (xy.x - sw.x) / uSwellWidth;
    float v = (xy.y - sw.y) / (uSwellWidth * 3.5);
    float bump = sw.z * exp(-u * u - v * v);
    h += bump;
    dx += bump * (-2.0 * u / uSwellWidth);
    dy += bump * (-2.0 * v / (uSwellWidth * 3.5));
  }
  return vec3(h, dx, dy);
}

float march(vec3 origin, vec3 dir) {
  float dist = 0.0;
  for (int i = 0; i < 128; i++) {
    if (float(i) >= uSteps) break;
    float gap = origin.z + dist * dir.z - surface(origin.xy + dist * dir.xy).x;
    if (abs(gap) < 0.002 * dist + 0.01) break;
    dist += uStride * gap;
    if (!(abs(dist) < FAR)) return FAR;
  }
  return dist;
}

void main() {
  float c;
  float s;
  float vfov = (3.14159 / 2.3) / max(uZoom, 0.05);
  vec3 cam = vec3(0.0, 0.0, ${CAMERA_HEIGHT.toFixed(1)});
  vec2 uv = gl_FragCoord.xy / uResolution - 0.5;
  uv.x *= uResolution.x / uResolution.y;
  uv.y *= -1.0;

  vec3 dir = vec3(0.0, 0.0, -1.0);
  float ulen = length(uv);
  float xrot = vfov * ulen;
  c = cos(xrot);
  s = sin(xrot);
  dir = mat3(1.0, 0.0, 0.0, 0.0, c, -s, 0.0, s, c) * dir;
  vec2 nuv = ulen > 1e-5 ? uv / ulen : vec2(1.0, 0.0);
  dir = mat3(nuv.x, -nuv.y, 0.0, nuv.y, nuv.x, 0.0, 0.0, 0.0, 1.0) * dir;
  c = cos(uTilt + uLook.y);
  s = sin(uTilt + uLook.y);
  dir = mat3(c, 0.0, s, 0.0, 1.0, 0.0, -s, 0.0, c) * dir;
  c = cos(uLook.x);
  s = sin(uLook.x);
  dir = mat3(c, s, 0.0, -s, c, 0.0, 0.0, 0.0, 1.0) * dir;

  float dist = march(cam, dir);
  vec3 pos = cam + dist * dir;
  vec3 field = surface(pos.xy);
  vec3 view = -dir;

  float crest = clamp((field.x - uHeight) / max(2.0 * uAmplitude, 0.001) * 0.5 + 0.5, 0.0, 1.0);
  vec3 body = mix(uWave, uCrest, smoothstep(0.45, 1.0, crest) * 0.5);
  vec3 normal = normalize(vec3(-field.yz * (1.0 + 1.5 * uRelief), 1.0));
  float diffuse = clamp(dot(normal, uSun) * 0.5 + 0.5, 0.0, 1.0);
  float lightness = mix(1.0, diffuse * diffuse * (3.0 - 2.0 * diffuse), uRelief);
  float sheen = pow(clamp(dot(normal, normalize(uSun + view)), 0.0, 1.0), 14.0) * uSheen * (0.4 + 0.6 * uRelief);
  float rim = pow(1.0 - clamp(dot(normal, view), 0.0, 1.0), 3.0);

  vec3 col;
  float alpha;
  float haze = 1.0 - exp(-dist / (uFogDepth * 5.0));
  float fade = 1.0 - smoothstep(0.35, 1.0, haze);
  if (uLight < 0.5) {
    vec3 lit = mix(uHorizon * 0.18, body, lightness) + uCrest * sheen;
    lit = mix(lit, uHorizon, rim * 0.15 * uRelief);
    col = mix(lit, uHorizon, smoothstep(0.0, 1.0, haze)) * 0.85 * uBrightness;
    alpha = fade;
  } else {
    vec3 hue = mix(uHorizon, body, 0.35 + 0.65 * lightness);
    float density = mix(0.16, 0.9, 1.0 - lightness);
    density *= 1.0 - clamp(sheen, 0.0, 1.0) * 0.85;
    density *= 1.0 - 0.5 * smoothstep(0.0, 1.0, haze);
    col = hue;
    alpha = fade * clamp(density * uBrightness, 0.0, 1.0);
  }

  float reveal = uIntro.x;
  col = clamp(col, 0.0, 1.0);
  // Static grain avoids the old one-second texture reset and repeats exactly.
  float grain = (hash(gl_FragCoord.xy) - 0.5) * uGrain;
  alpha = clamp(alpha * (1.0 + grain), 0.0, 1.0) * reveal * uOpacity;
  if (uOpaque > 0.5) {
    outColor = vec4(mix(uBackground, col, alpha), 1.0);
  } else {
    outColor = vec4(col * alpha, alpha);
  }
}`;

type GradientWavesProps = {
  horizonColor?: string;
  waveColor?: string;
  crestColor?: string;
  speed?: number;
  amplitude?: number;
  waveScale?: number;
  swell?: number;
  turbulence?: number;
  fogDepth?: number;
  relief?: number;
  lightAngle?: number;
  paused: boolean;
  quality?: keyof typeof QUALITY;
  dpr?: number;
};

// Site palette colors are hex; invalid values fall back to the supplied palette.
const rgb = (value: string, fallback: string) => {
  const hex = /^#[0-9a-f]{6}$/i.test(value) ? value : fallback;
  const n = parseInt(hex.slice(1), 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
};

const compile = (gl: WebGL2RenderingContext, type: number, source: string) => {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }
  return shader;
};

export default function GradientWaves({
  horizonColor = '#11064e', waveColor = '#347ed3', crestColor = '#c4e1ff',
  speed = 0.35, amplitude = 2.6, waveScale = 0.7, swell = 38,
  turbulence = 24, fogDepth = 12, relief = 1, lightAngle = 170,
  paused, quality = 'medium', dpr = 1.25,
}: GradientWavesProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const wakeRef = useRef<(() => void) | null>(null);
  const settingsRef = useRef({ paused });
  settingsRef.current = { paused };

  useEffect(() => { wakeRef.current?.(); }, [paused]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const canvas = document.createElement('canvas');
    canvas.className = 'gradient-waves-canvas';
    container.appendChild(canvas);
    const gl = canvas.getContext('webgl2', {
      alpha: true, antialias: false, depth: false, stencil: false,
      premultipliedAlpha: true, powerPreference: 'low-power',
    });
    if (!gl) { canvas.remove(); return; }
    const vertex = compile(gl, gl.VERTEX_SHADER, VERTEX);
    const fragment = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT);
    const program = gl.createProgram();
    if (!vertex || !fragment || !program) {
      if (vertex) gl.deleteShader(vertex);
      if (fragment) gl.deleteShader(fragment);
      if (program) gl.deleteProgram(program);
      canvas.remove();
      return;
    }
    gl.attachShader(program, vertex);
    gl.attachShader(program, fragment);
    gl.bindAttribLocation(program, 0, 'aPosition');
    gl.linkProgram(program);
    gl.deleteShader(vertex);
    gl.deleteShader(fragment);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      gl.deleteProgram(program);
      canvas.remove();
      return;
    }
    const vao = gl.createVertexArray();
    const buffer = gl.createBuffer();
    if (!vao || !buffer) {
      gl.deleteVertexArray(vao);
      gl.deleteBuffer(buffer);
      gl.deleteProgram(program);
      canvas.remove();
      return;
    }
    gl.bindVertexArray(vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.useProgram(program);

    const uniform = (name: string) => gl.getUniformLocation(program, name);
    const resolution = uniform('uResolution');
    const phase = uniform('uPhase');
    const scale = Math.max(0.01, waveScale);
    const waveHeight = Math.max(0, amplitude);
    const angle = lightAngle * Math.PI / 180;
    const slope = waveHeight * ((scale / 7) * (1 + Math.abs(swell) / 20) +
      ((scale * 0.9) / 3) * (1 + Math.abs(turbulence) / 23));
    const constants: Record<string, number> = {
      uAmplitude: waveHeight, uWaveScale: scale, uWaveRatio: 0.9, uSwell: swell,
      uTurbulence: turbulence, uHeight: 5.5, uTilt: 64 * Math.PI / 180,
      uZoom: 1, uFogDepth: Math.max(0.5, fogDepth), uBrightness: 1,
      uOpacity: 1, uGrain: 0.04, uOpaque: 0, uLight: 0,
      uRelief: Math.min(1, Math.max(0, relief)), uSheen: 0,
      uSteps: QUALITY[quality], uStride: 0.9 / Math.max(1, slope / 1.5), uSwellWidth: 8,
    };
    Object.entries(constants).forEach(([name, value]) => gl.uniform1f(uniform(name), value));
    gl.uniform2f(uniform('uLook'), 0, 0);
    gl.uniform3fv(uniform('uHorizon'), rgb(horizonColor, '#11064e'));
    gl.uniform3fv(uniform('uWave'), rgb(waveColor, '#347ed3'));
    gl.uniform3fv(uniform('uCrest'), rgb(crestColor, '#c4e1ff'));
    gl.uniform3f(uniform('uBackground'), 0, 0, 0);
    gl.uniform3f(uniform('uSun'), Math.cos(0.52) * Math.cos(angle), Math.cos(0.52) * Math.sin(angle), Math.sin(0.52));
    gl.uniform3f(uniform('uIntro'), 1, 3, 0);
    gl.uniform3fv(uniform('uSwells[0]'), new Float32Array(MAX_SWELLS * 3));

    let raf = 0;
    let last = 0;
    let elapsed = 0;
    let visible = false;
    let lost = false;

    const fit = () => {
      const { clientWidth: width, clientHeight: height } = container;
      // Bound GPU work on large / Retina displays while keeping the canvas full size.
      const budgetRatio = Math.sqrt(MAX_PIXELS / Math.max(1, width * height));
      const ratio = Math.min(window.devicePixelRatio || 1, Math.max(0.5, dpr), budgetRatio);
      const renderWidth = Math.max(1, Math.floor(width * ratio));
      const renderHeight = Math.max(1, Math.floor(height * ratio));
      if (canvas.width !== renderWidth || canvas.height !== renderHeight) {
        canvas.width = renderWidth;
        canvas.height = renderHeight;
      }
    };
    const draw = () => {
      fit();
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.uniform2f(resolution, canvas.width, canvas.height);
      gl.uniform1f(phase, (elapsed / LOOP_SECONDS) * Math.PI * 2);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    };
    const tick = (now: number) => {
      raf = 0;
      if (!visible || document.hidden || lost) return;
      const stopped = settingsRef.current.paused;
      const delta = last ? now - last : 0;
      if (!stopped && last && delta < FRAME_INTERVAL) {
        raf = requestAnimationFrame(tick);
        return;
      }
      if (!stopped) {
        const step = Math.min(delta / 1000, 0.1) * (speed / REFERENCE_SPEED);
        // Keep the clock bounded without a visual reset, even after hours open.
        elapsed = ((elapsed + step) % LOOP_SECONDS + LOOP_SECONDS) % LOOP_SECONDS;
      }
      last = now;
      draw();
      if (!stopped) raf = requestAnimationFrame(tick);
    };
    const wake = () => {
      // Reset the wall clock on resume; neither hidden time nor paused time advances waves.
      cancelAnimationFrame(raf);
      raf = 0;
      last = 0;
      if (visible && !document.hidden && !lost) raf = requestAnimationFrame(tick);
    };
    wakeRef.current = wake;
    const visibility = new IntersectionObserver(entries => {
      visible = entries.some(entry => entry.isIntersecting);
      wake();
    });
    const sizes = new ResizeObserver(wake);
    const onContextLost = () => {
      lost = true;
      canvas.hidden = true;
      wake();
    };
    visibility.observe(container);
    sizes.observe(container);
    document.addEventListener('visibilitychange', wake);
    canvas.addEventListener('webglcontextlost', onContextLost);

    return () => {
      cancelAnimationFrame(raf);
      wakeRef.current = null;
      visibility.disconnect();
      sizes.disconnect();
      document.removeEventListener('visibilitychange', wake);
      canvas.removeEventListener('webglcontextlost', onContextLost);
      gl.deleteProgram(program);
      gl.deleteBuffer(buffer);
      gl.deleteVertexArray(vao);
      gl.getExtension('WEBGL_lose_context')?.loseContext();
      canvas.remove();
    };
  }, [horizonColor, waveColor, crestColor, speed, amplitude, waveScale, swell,
    turbulence, fogDepth, relief, lightAngle, quality, dpr]);

  return <div className="gradient-waves-container" ref={containerRef} />;
}
